/*  iceberg-ui  */
import { Icon, InputText } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Badge,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    Menu,
    Radio,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Column, HeaderContext, Table } from '@tanstack/react-table';

/*  Shared  */
import {
    TooltipPlacement,
    TooltipQ
} from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { multipleKeyTranslation } from 'application/Shared/Helpers';
import { Icons } from 'application/Shared/Icons/LocalizationIcons';

/*  React  */
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SimpleHeader } from './SimpleHeader';
import { ColumnDef, FilterOptions as ColumnFilterOptions } from './types';

/**
 * The minimal number of options to display the search input.
 */
const MINIMAL_SEARCHABLE_OPTIONS = 24;

/**
 * Controls object maps control types to their respective React components.
 */
const Controls: Record<
    string,
    (props: {
        value: string;
        name: string;
        checked: boolean;
        size: 'small' | 'medium';
        sx: { padding: string };
    }) => JSX.Element
> = {
    Checkbox,
    Radio
};

/**
 * OptionControl component renders a form control label with a customizable control element.
 * It supports internationalization and optional tooltips for each option.
 *
 * @param {Object} props - The properties for the OptionControl component.
 * @returns {JSX.Element} A FormControlLabel component with the specified control and label.
 *
 * @example
 * <OptionControl
 *   columnId="culumnA"
 *   option="optionValue"
 *   checked={true}
 *   control="checkbox"
 * />
 */
export const OptionControl = ({
    columnId,
    option,
    checked,
    control
}: {
    /**
     * The unique identifier for the column for which we will filter.
     */
    columnId: string;
    /**
     * The specific option value.
     */
    option: string;
    /**
     * Whether the option is currently selected.
     */
    checked: boolean;
    /**
     * The type of control to render (must be a key in the Controls object).
     */
    control: keyof typeof Controls;
}) => {
    const { i18n, t } = useTranslation();

    const Control = Controls[control];

    return (
        <FormControlLabel
            key={option}
            control={
                <Control
                    value={option}
                    name="filter"
                    checked={checked}
                    size="small"
                    sx={{ padding: '6px 8px' }}
                />
            }
            label={
                <Box display="flex" alignItems="center">
                    <Trans
                        i18nKey={multipleKeyTranslation(
                            i18n,
                            [
                                `Table.filter.${columnId}.${option}`,
                                `Table.filter.${option}`
                            ],
                            option
                        )}
                        components={Icons}
                    />
                    {/* We check if there is a tooltip text for the given option */}
                    {i18n.exists(
                        `Table.filter.${columnId}.${option}.tooltip`
                    ) && (
                        <TooltipQ
                            placement={TooltipPlacement.RIGHT}
                            text={t(
                                `Table.filter.${columnId}.${option}.tooltip`
                            )}
                        />
                    )}
                </Box>
            }
        />
    );
};

/**
 * FilterOptions component renders a list of filter options, optionally grouped.
 * It supports internationalization for group labels and renders individual options using OptionControl.
 *
 * @param {Object} props - The properties for the FilterOptions component.
 * @returns {JSX.Element[]} An array of React elements representing the filter options.
 *
 * @example
 * <FilterOptions
 *   options={[
 *     { group: 'Status', options: ['Active', 'Inactive'] },
 *     { options: ['Option1', 'Option2'] }
 *   ]}
 *   columnId="status"
 *   selectedOptions={['Active']}
 *   control="checkbox"
 * />
 */
export const FilterOptions = ({
    options,
    columnId,
    selectedOptions,
    control,
    search
}: {
    /**
     * Array of option groups to be rendered.
     */
    options: ColumnFilterOptions[];
    /**
     * Identifier for the column being filtered.
     */
    columnId: string;
    /**
     * Array of currently selected option values.
     */
    selectedOptions: string[];
    /**
     * Type of control to use for options (must be a key in the Controls object).
     */
    control: keyof typeof Controls;
    /**
     * Search string to filter options.
     */
    search: string;
}) => {
    const { t } = useTranslation();

    return options?.map(({ group, options }: ColumnFilterOptions) => (
        <React.Fragment key={options.toString()}>
            {group && (
                <Typography
                    component="span"
                    style={{
                        fontSize: FontSizes[1],
                        fontWeight: 600,
                        margin: '8px 0 2px 0'
                    }}
                >
                    {t(`Table.filter.${columnId}.${group}.group`)}
                </Typography>
            )}

            {options
                .filter((option) =>
                    option
                        .toString()
                        .toLowerCase()
                        .includes(search.toLowerCase())
                )
                .map((option) => (
                    <OptionControl
                        key={option}
                        columnId={columnId}
                        option={option}
                        checked={selectedOptions.includes(option.toString())}
                        control={control}
                    />
                ))}
        </React.Fragment>
    ));
};

/**
 * Filters component renders a filter button and menu for a table column.
 * It manages the state of filter options and handles filter application and clearing.
 *
 * Features:
 * - Dynamically generates filter options based on column values if no defaultOptions are provided
 * - Supports custom filter options defined in column metadata
 * - Allows selection of multiple filter values
 * - Provides a clear filters option
 *
 * @template TData
 * @param {FiltersProps<TData>} props - The properties for the Filters component.
 * @returns {JSX.Element} A React fragment containing the filter button and menu.
 *
 * @example
 * <Filters<TData>
 *   column={columnInstance}
 *   table={tableInstance}
 * />
 */
export const Filters = <TData, TValue>({
    column,
    table
}: {
    /**
     * The column instance for which we are rendering filters.
     */
    column: Column<TData>;
    /**
     * The table instance for which we are rendering filters.
     */
    table: Table<TData>;
}) => {
    // We are using the columnDef.meta object to pass the filter options
    const columnDef = column.columnDef as ColumnDef<TData, TValue>;
    const { defaultOptions = [], control = 'Checkbox' } =
        columnDef.meta?.filter ?? {};
    const { t } = useTranslation();
    const theme = useTheme();

    // The search string for filtering options.
    const [search, setSearch] = useState('');

    // Get the current columnFilters of the table state.
    const { columnFilters } = table.getState();

    /**
     * The filtered rows from the table.
     */
    const { rows } = table.getPreFilteredRowModel();

    /**
     * State for the anchor element of the filter menu.
     */
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    /**
     * State for the accumulated options for the filter based on the values in the column.
     */
    const [accumulatedOptions, setAccumulatedOptions] =
        React.useState<ColumnFilterOptions[]>(defaultOptions);

    /**
     * We set the form data based on the current filter value.
     */
    const selectedOptions = (column.getFilterValue() as string[]) || [];

    // TODO: Handle better defaultOptions and accumulatedOptions
    React.useEffect(() => {
        if (rows.length) {
            const includeNullValue = !!columnDef.meta?.filter?.includeNullValue;
            setAccumulatedOptions((prevOptions: ColumnFilterOptions[]) => {
                const newOptions = Array.from(
                    new Set([
                        ...prevOptions.flatMap((p) => p.options),
                        ...Array.from(column.getFacetedUniqueValues().keys()),
                        // Include "Null" as a filter option for empty values.
                        // Capitalized so that it gets sorted properly.
                        // It will be translated to 'None' via 'Table.filter.Null'
                        ...(includeNullValue ? ['Null'] : [])
                    ])
                )
                    .filter((item) => item !== null && item !== undefined) // Sometimes we want to show the "False" option
                    .sort();
                return [{ options: newOptions }];
            });
        }
    }, [rows, column, columnDef]);

    /**
     * Updates the column filters based on the selected options.
     *
     * @param {React.FormEvent<HTMLFormElement>} event - The form event.
     */
    const handleFormChange = (event: React.FormEvent<HTMLFormElement>) => {
        const formData = new FormData(event.currentTarget);
        const selectedValues = formData.getAll('filter');

        // We need to remove the "old" one and add the new one.
        const filteredFilters = columnFilters?.filter(
            (filter) => filter.id !== column.id
        );
        table.setColumnFilters([
            ...filteredFilters,
            {
                id: column.id,
                value:
                    // If the control is a checkbox, we want to return an array of selected values
                    // If it is not a checkbox, then it can be a radio button, which means we only want the first element of the array, as an array.
                    control === 'Checkbox'
                        ? selectedValues
                        : [selectedValues[0]]
            }
        ]);
    };

    /**
     * Clears the column filters.
     */
    const handleClearFilters = () => {
        const filteredFilters = columnFilters?.filter(
            (filter) => filter.id !== column.id
        );

        table.setColumnFilters([...filteredFilters]);
    };

    const isNone =
        defaultOptions?.length === 1 &&
        defaultOptions[0].options.includes('None');

    const filterOptions =
        defaultOptions?.length && !isNone ? defaultOptions : accumulatedOptions;

    return (
        <>
            <IconButton
                onClick={(
                    event:
                        | React.MouseEvent<HTMLButtonElement, MouseEvent>
                        | React.TouchEvent<HTMLButtonElement>
                ) => {
                    setAnchorEl(event.currentTarget);
                }}
                size="small"
                sx={{ color: 'icon.textColor' }}
                variant="slim"
                aria-label="Filter"
                data-testid={`tableFilter_${column.id}`}
                data-trackid={`tableFilter_${column.id}_btn`}
            >
                <Badge
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    badgeContent={selectedOptions.length ? 1 : 0}
                    color="secondary"
                    overlap="rectangular"
                    sx={{
                        // Has to match anchorOrigin above
                        '& .MuiBadge-anchorOriginBottomRight': {
                            bottom: '2px'
                        }
                    }}
                    variant="dot"
                >
                    <Icon size={FontSizes[1]} src="Filter" />
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                slotProps={{
                    paper: {
                        sx: {
                            boxShadow: theme.shadows[24],
                            minWidth: '300px',
                            padding: '16px'
                        }
                    }
                }}
                onClose={() => {
                    setAnchorEl(null);
                }}
                MenuListProps={{ style: { padding: 0 } }}
                variant="menu"
                data-testid={`tableFilterMenu_${column.id}`}
            >
                <Typography
                    component="p"
                    sx={{
                        fontSize: FontSizes[2],
                        fontWeight: 600,
                        marginBottom: '8px'
                    }}
                >
                    {t('Table.filter.header', {
                        columnHeaderKey: (
                            column.columnDef as ColumnDef<TData, TValue>
                        ).name
                    })}
                </Typography>

                {/* The search input will display when there are more than MINIMAL_SEARCHABLE_OPTIONS(= 24) different options for filtering */}
                {(defaultOptions[0]?.options?.length >
                    MINIMAL_SEARCHABLE_OPTIONS ||
                    (defaultOptions?.length === 0 &&
                        accumulatedOptions[0]?.options.length >
                            MINIMAL_SEARCHABLE_OPTIONS)) && (
                    <InputText
                        placeholder={t('Common.search')}
                        onChange={(
                            event: React.FormEvent<HTMLInputElement>
                        ) => {
                            setSearch(event.currentTarget.value);
                        }}
                        sx={{
                            display: 'block',
                            marginBottom: '8px',
                            width: '100%'
                        }}
                    />
                )}

                <FormControl
                    component="form"
                    onChange={handleFormChange}
                    style={{
                        maxHeight: '70vh',
                        overflow: 'auto',
                        padding: '2px',
                        width: '100%'
                    }}
                >
                    <FormGroup>
                        <FilterOptions
                            options={filterOptions}
                            columnId={column.id}
                            selectedOptions={selectedOptions}
                            control={control}
                            search={search}
                        />
                    </FormGroup>
                </FormControl>

                <Button
                    data-trackid={`${column.id}_clearFilterBtn`}
                    disabled={selectedOptions.length === 0}
                    onClick={handleClearFilters}
                    sx={{ marginTop: '8px', padding: 0 }}
                    variant="text"
                >
                    {t('Table.filters.clear')}
                </Button>
            </Menu>
        </>
    );
};

/**
 * Header component for the table.
 *
 * @param column - The column object
 * @param table - The table object
 */
export const Header = <TData, TValue>({
    column,
    table
}: HeaderContext<TData, unknown>): React.ReactElement => {
    const columnDef = column.columnDef as ColumnDef<TData, TValue>;

    return (
        <>
            <SimpleHeader
                text={columnDef.name!}
                tooltip={columnDef.tooltipText}
                justifyContent={
                    columnDef.meta?.header?.justifyContent ?? 'flex-start'
                }
                icon={columnDef.meta?.icon}
            />
            {column.getCanFilter() && (
                <Filters<TData, TValue> column={column} table={table} />
            )}
        </>
    );
};
